import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { 
  Sparkles, Calculator, Clock, TrendingUp, Building2, MapPin, Crown, Ruler, 
  IndianRupee, ChevronDown, ChevronRight, Lightbulb, AlertCircle, CheckCircle2,
  AlertTriangle, FileText, ListChecks, Wrench, Shield, Building, Bed, Bath,
  Monitor, Users, Factory, Box, FolderOpen, Trash2, Briefcase, Gift, Zap, Home, ChefHat, Lock
} from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
// iter-366 — reuse the same AI Task Creator dialog (3 modes) so the AI
// Assistant page has architecture + UI parity with the Edit BOQ launcher.
import AITaskCreatorDialog from '@/components/boq/AITaskCreatorDialog';
import { Wand2, Building2 as WsIcon, Image as FileIcon } from 'lucide-react';
// iter-359g — pull the shared 7-option taxonomy so this page + the
// AI Task Creator dialog always stay in sync.
import { PROJECT_TYPES } from '@/lib/project_types';

// iter-359g — PROJECT_TYPES imported from `/lib/project_types.js` (shared with AITaskCreatorDialog).

const QUALITY_TIERS = [
  { value: 'budget', label: 'Budget', description: 'Basic materials', range: '₹1,000-1,500/sqft', color: 'bg-stone-100' },
  { value: 'standard', label: 'Standard', description: 'Good quality', range: '₹1,500-2,500/sqft', color: 'bg-blue-100' },
  { value: 'premium', label: 'Premium', description: 'Premium finishes', range: '₹2,500-3,750/sqft', color: 'bg-amber-100' },
  { value: 'luxury', label: 'Luxury', description: 'Imported materials', range: '₹3,750+/sqft', color: 'bg-purple-100' },
];

export default function AIBudgetEstimation() {
  const [mode, setMode] = useState('budget');
  const [loading, setLoading] = useState(false);
  const [budgetResult, setBudgetResult] = useState(null);
  const [timelineResult, setTimelineResult] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showTerms, setShowTerms] = useState(false);
  // iter-366 — AI Task Creator embedded on AI Assistant page.
  const [aiTaskDialogOpen, setAiTaskDialogOpen] = useState(false);
  const [aiTaskMode, setAiTaskMode] = useState('single');
  const [aiTaskWorksheetId, setAiTaskWorksheetId] = useState('');
  const [availableWorksheets, setAvailableWorksheets] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/boq-worksheets?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.worksheets || []);
        setAvailableWorksheets(rows.map((w) => ({
          id: w.id,
          label: `${w.worksheet_number || 'BOQ'} · ${w.project_name || w.name || 'Unknown project'}`,
        })));
      })
      .catch(() => setAvailableWorksheets([]));
  }, []);
  const openAiTaskCreator = (m) => {
    if (!aiTaskWorksheetId) {
      toast.error('Select a BOQ worksheet first');
      return;
    }
    setAiTaskMode(m);
    setAiTaskDialogOpen(true);
  };
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [confirmConvertOpen, setConfirmConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [usage, setUsage] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const breakdownRef = useRef(null);
  
  // Credit system state
  const [credits, setCredits] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [purchasingPack, setPurchasingPack] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [creditRequestReason, setCreditRequestReason] = useState('');
  const [creditsRequested, setCreditsRequested] = useState(5);

  // Fetch usage and credits on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usageRes, creditsRes] = await Promise.all([
          axios.get(`${API}/ai/usage`).catch(() => ({ data: null })),
          axios.get(`${API}/billing/my-credits`).catch(() => ({ data: null }))
        ]);
        setUsage(usageRes.data);
        // Bug 2: unified AI Credits wallet (same balance as Billing & Plans).
        setCredits(creditsRes.data ? { ...creditsRes.data, credits: creditsRes.data.unlimited ? Infinity : (creditsRes.data.balance ?? 0) } : null);
      } catch (e) {
        console.error('Failed to fetch data');
      }
    };
    fetchData();
  }, [budgetResult]);
  
  // Fetch credit packs
  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const res = await axios.get(`${API}/ai/credit-packs`);
        setCreditPacks(res.data.packs || []);
      } catch (e) {
        console.error('Failed to fetch credit packs');
      }
    };
    fetchPacks();
  }, []);
  
  const [budgetForm, setBudgetForm] = useState({
    project_type: 'residential_apartment',
    area_sqft: '',
    location: '',
    quality_tier: 'standard',
    // Residential
    bedrooms: '',
    bathrooms: '',
    // Commercial
    workstations: '',
    cabins: '',
    conference_rooms: '',
    // Industrial
    production_zones: '',
    storage_areas: '',
    office_area_sqft: '',
    // Common
    floor_number: '',
    has_lift_access: true,
    special_requirements: '',
    // Power Backup (MANDATORY QUESTION)
    requires_power_backup: null, // null = not answered, true = yes, false = no
    backup_time_minutes: 15, // Default 15 minutes
    // Home Automation (OPTIONAL - MUST ASK)
    wants_home_automation: null, // null = not answered, true = yes, false = no
    home_automation_tier: 'standard',
    // Modular Kitchen (OPTIONAL - SEPARATE FROM CARPENTRY)
    wants_modular_kitchen: null, // null = not answered, true = yes, false = no
    modular_kitchen_tier: 'standard',
    kitchen_running_ft: 10, // Default 10 running ft
  });

  const [timelineForm, setTimelineForm] = useState({
    project_type: 'residential_apartment',
    area_sqft: '',
    budget: '',
    priority: 'standard',
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isInternal = ['owner', 'staff', 'project_manager', 'admin'].includes(user.role);
  const isClient = user.role === 'client';
  const canAccess = isInternal || isClient;
  const hasCredits = isInternal || (credits?.credits > 0);

  const isResidential = ['residential_apartment', 'residential_villa', 'bare_shell', 'renovation'].includes(budgetForm.project_type);
  const isCommercial = ['commercial', 'retail_showroom'].includes(budgetForm.project_type);
  const isIndustrial = budgetForm.project_type === 'industrial';

  const handleBudgetEstimation = async () => {
    if (!budgetForm.area_sqft || !budgetForm.location) {
      toast.error('Please fill in area and location');
      return;
    }

    setLoading(true);
    setTermsAccepted(false);
    setBudgetResult(null);
    setSelectedCategories(new Set()); // Reset selection on new estimate
    try {
      const payload = {
        ...budgetForm,
        area_sqft: parseFloat(budgetForm.area_sqft),
        bedrooms: budgetForm.bedrooms ? parseInt(budgetForm.bedrooms) : null,
        bathrooms: budgetForm.bathrooms ? parseInt(budgetForm.bathrooms) : null,
        workstations: budgetForm.workstations ? parseInt(budgetForm.workstations) : null,
        cabins: budgetForm.cabins ? parseInt(budgetForm.cabins) : null,
        conference_rooms: budgetForm.conference_rooms ? parseInt(budgetForm.conference_rooms) : null,
        production_zones: budgetForm.production_zones ? parseInt(budgetForm.production_zones) : null,
        storage_areas: budgetForm.storage_areas ? parseInt(budgetForm.storage_areas) : null,
        office_area_sqft: budgetForm.office_area_sqft ? parseFloat(budgetForm.office_area_sqft) : null,
        floor_number: parseInt(budgetForm.floor_number) || 0,
      };
      
      const response = await axios.post(`${API}/ai/budget-estimation`, payload);
      setBudgetResult(response.data);
      // Select all categories by default
      const allCategories = new Set((response.data.budget_breakdown || []).map(c => c.category));
      if (response.data.contingency) {
        allCategories.add('Contingency');
      }
      setSelectedCategories(allCategories);
      toast.success('Budget estimation generated!');
      window.dispatchEvent(new Event('bc:credits-changed'));
      
    } catch (error) {
      if (error.response?.status === 402) {
        // Bug 2: the global paywall (Starter / Growth / Studio packs) opens via the axios interceptor.
        toast.error('Budget estimation needs 100 AI credits');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate budget');
      }
    } finally {
      setLoading(false);
    }
  };

  // Purchase credit pack via Razorpay
  const handlePurchasePack = async (pack) => {
    setPurchasingPack(pack.id);
    try {
      const orderRes = await axios.post(`${API}/ai/credits/create-order`, { pack_id: pack.id });
      const { order_id, amount, key_id } = orderRes.data;
      
      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'Bella Casa AI Credits',
        description: pack.description,
        order_id: order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/ai/credits/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setCredits(prev => ({ ...prev, credits: verifyRes.data.credits }));
            toast.success(verifyRes.data.message);
            setShowCreditDialog(false);
          } catch (e) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#7c3aed'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment order');
    } finally {
      setPurchasingPack(null);
    }
  };

  // Request credits from admin
  const handleRequestCredits = async () => {
    if (!creditRequestReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await axios.post(`${API}/ai/credits/request`, {
        credits_requested: creditsRequested,
        reason: creditRequestReason
      });
      toast.success('Credit request submitted! Admin will review shortly.');
      setShowRequestDialog(false);
      setCreditRequestReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const toggleCategorySelection = (categoryName) => {
    setSelectedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const getSelectedTotal = () => {
    if (!budgetResult) return 0;
    let total = 0;
    (budgetResult.budget_breakdown || []).forEach(item => {
      if (selectedCategories.has(item.category)) {
        total += item.amount;
      }
    });
    if (budgetResult.contingency && selectedCategories.has('Contingency')) {
      total += budgetResult.contingency.amount;
    }
    return total;
  };

  const handleTimelineEstimation = async () => {
    if (!timelineForm.area_sqft || !timelineForm.budget) {
      toast.error('Please fill in area and budget');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/timeline-estimation`, {
        ...timelineForm,
        area_sqft: parseFloat(timelineForm.area_sqft),
        budget: parseFloat(timelineForm.budget),
      });
      setTimelineResult(response.data);
      toast.success('Timeline generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate timeline');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleConvertToProject = async () => {
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    if (!clientName.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    if (!contactDetails.trim()) {
      toast.error('Please enter contact details');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setConverting(true);
    try {
      // Convert selected categories Set to Array for API
      const selectedCategoriesArray = Array.from(selectedCategories);
      
      const response = await axios.post(`${API}/ai/convert-to-project`, {
        estimation_id: budgetResult.estimation_id,
        project_name: projectName,
        client_name: clientName,
        contact_details: contactDetails,
        tc_accepted: true,
        selected_categories: selectedCategoriesArray, // Only create tasks for selected categories
      });
      
      toast.success(`Project "${response.data.project_name}" created with ${response.data.tasks_created} tasks!`);
      setShowConvertDialog(false);
      
      // Navigate to the new project
      window.location.href = `/projects/${response.data.project_id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to project');
    } finally {
      setConverting(false);
    }
  };

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-16">
          <AlertCircle className="mx-auto mb-4 text-stone-300" size={64} />
          <h2 className="text-xl font-bold text-stone-700 mb-2">Access Restricted</h2>
          <p className="text-stone-500">AI Budget Estimation is available for authorized users only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="ai-budget-page">
        {/* iter-366 — AI Task Creator (parity with Edit BOQ) — pick a BOQ
            worksheet then launch Single/Multi Item, Full Project BOQ, or
            Upload from File (image or PDF). */}
        <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-purple-50 p-4" data-testid="ai-assistant-task-creator-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <h3 className="font-semibold text-stone-900 flex items-center gap-2 text-base">
                <Sparkles size={16} className="text-violet-600" />
                AI Creator
              </h3>
              <p className="text-xs text-stone-600 mt-0.5">Same 3-mode engine as Edit BOQ. Pick a worksheet, then launch a mode.</p>
            </div>
            <div className="min-w-[260px]">
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Target Worksheet</Label>
              <Select value={aiTaskWorksheetId} onValueChange={setAiTaskWorksheetId}>
                <SelectTrigger className="bg-white mt-0.5" data-testid="ai-assistant-worksheet-select">
                  <SelectValue placeholder={availableWorksheets.length ? 'Select a BOQ worksheet' : 'No worksheets accessible'} />
                </SelectTrigger>
                <SelectContent>
                  {availableWorksheets.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => openAiTaskCreator('single')}
              disabled={!aiTaskWorksheetId}
              className="text-left p-3 rounded-lg bg-white border border-stone-200 hover:border-violet-400 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ai-assistant-mode-single"
            >
              <div className="flex items-center gap-2 mb-1">
                <Wand2 size={14} className="text-violet-600" />
                <span className="text-sm font-semibold text-stone-900">Single / Multi Item</span>
              </div>
              <p className="text-[11px] text-stone-500">Describe items in plain English. AI infers rates, categories, units and dimensions from your past BOQs.</p>
            </button>
            <button
              type="button"
              onClick={() => openAiTaskCreator('full')}
              disabled={!aiTaskWorksheetId}
              className="text-left p-3 rounded-lg bg-white border border-stone-200 hover:border-violet-400 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ai-assistant-mode-full"
            >
              <div className="flex items-center gap-2 mb-1">
                <WsIcon size={14} className="text-violet-600" />
                <span className="text-sm font-semibold text-stone-900">Full Project BOQ</span>
              </div>
              <p className="text-[11px] text-stone-500">Auto-generate a complete BOQ by budget, area, quality tier and project type — chunked, streamable.</p>
            </button>
            <button
              type="button"
              onClick={() => openAiTaskCreator('image')}
              disabled={!aiTaskWorksheetId}
              className="text-left p-3 rounded-lg bg-white border border-stone-200 hover:border-violet-400 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ai-assistant-mode-file"
            >
              <div className="flex items-center gap-2 mb-1">
                <FileIcon size={14} className="text-violet-600" />
                <span className="text-sm font-semibold text-stone-900">Upload from File</span>
              </div>
              <p className="text-[11px] text-stone-500">Drop a site photo, sketch, PDF brief or spec sheet. AI extracts 3-12 line items.</p>
            </button>
          </div>
        </div>
        {aiTaskDialogOpen && aiTaskWorksheetId && (
          <AITaskCreatorDialog
            open={aiTaskDialogOpen}
            onOpenChange={setAiTaskDialogOpen}
            worksheetId={aiTaskWorksheetId}
            initialMode={aiTaskMode}
            onItemsCommitted={() => toast.success('Items added to the selected BOQ worksheet')}
          />
        )}

        {/* Credit Banner for Clients */}
        {isClient && credits && (
          <div className={`rounded-xl p-4 flex items-center justify-between ${
            credits.credits > 0 
              ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white'
              : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
          }`}>
            <div className="flex items-center gap-3">
              <Zap className="flex-shrink-0" size={24} />
              <div>
                <p className="font-bold">
                  {credits.wallet_visible === false ? 'AI Budget Estimator' : credits.credits > 0 ? `${credits.credits} AI Credits Available` : 'No Credits Remaining'}
                </p>
                <p className="text-sm opacity-90">
                  {credits.credits > 0 
                    ? 'First estimation is free, then 100 AI Credits per estimation'
                    : (usage?.free_remaining > 0 ? 'Your first estimation is free' : 'Purchase AI Credits to continue')
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary"
                size="sm"
                onClick={() => window.dispatchEvent(new CustomEvent('bc:insufficient-credits', { detail: { message: 'Top up your AI Credits wallet.', required: 100, balance: credits?.balance ?? 0 } }))}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                data-testid="ai-budget-buy-credits"
              >
                <Zap size={14} className="mr-1" />
                Buy Credits
              </Button>
              <Button 
                variant="secondary"
                size="sm"
                onClick={() => setShowRequestDialog(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                Request Credits
              </Button>
            </div>
          </div>
        )}

        {/* Launch Offer Banner - For Internal Users */}
        {isInternal && usage && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="flex-shrink-0" size={24} />
              <div>
                <p className="font-bold">AI Budget Estimator</p>
                <p className="text-sm text-green-100">
                  {usage.is_unlimited
                    ? 'Unlimited estimations for your studio team.'
                    : usage.free_remaining > 0
                      ? 'Your first estimation is free. After that, 100 AI Credits per estimation.'
                      : '100 AI Credits per estimation.'
                  }
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{usage.total_estimations}</p>
              <p className="text-xs text-green-200">estimations used</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
                <Sparkles className="text-white" size={24} />
              </div>
              <h1 className="text-3xl font-bold text-stone-900">AI Project Assistant</h1>
            </div>
            <p className="text-stone-500">Smart, execution-ready budget estimations</p>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="bg-white rounded-xl border border-stone-200 p-2 inline-flex gap-2">
          <button
            onClick={() => setMode('budget')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              mode === 'budget' ? 'bg-violet-600 text-white shadow-md' : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Calculator size={18} />
            Budget Estimation
          </button>
          <button
            onClick={() => setMode('timeline')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              mode === 'timeline' ? 'bg-violet-600 text-white shadow-md' : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Clock size={18} />
            Timeline Planning
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form Section - 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-6 shadow-sm h-fit">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-violet-600" size={20} />
              Project Details
            </h2>

            {mode === 'budget' ? (
              <div className="space-y-4">
                {/* Project Type */}
                <div>
                  <Label className="text-stone-700 font-medium text-sm">Project Type</Label>
                  <Select 
                    value={budgetForm.project_type} 
                    onValueChange={(v) => setBudgetForm({ ...budgetForm, project_type: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="project-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <Icon size={14} className="text-stone-400" />
                              <span className="font-medium">{type.label}</span>
                              <span className="text-xs text-stone-400">({type.description})</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Area & Location */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-stone-700 font-medium text-sm">Area (sq.ft) *</Label>
                    <div className="relative mt-1">
                      <Ruler size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <Input
                        type="number"
                        value={budgetForm.area_sqft}
                        onChange={(e) => setBudgetForm({ ...budgetForm, area_sqft: e.target.value })}
                        placeholder="e.g. 1500"
                        className={`pl-9 bg-white ${!budgetForm.area_sqft ? 'border-amber-300' : ''}`}
                        data-testid="area-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-stone-700 font-medium text-sm">Location *</Label>
                    <div className="relative mt-1">
                      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <Input
                        value={budgetForm.location}
                        onChange={(e) => setBudgetForm({ ...budgetForm, location: e.target.value })}
                        placeholder="e.g. Mumbai, Delhi, Pune"
                        className={`pl-9 bg-white ${!budgetForm.location ? 'border-amber-300' : ''}`}
                        data-testid="location-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Dynamic Fields based on Project Type */}
                {isResidential && (
                  <div className="p-4 bg-blue-50 rounded-xl space-y-3">
                    <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <Building size={14} />
                      Residential Details
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-stone-600 text-xs">Bedrooms</Label>
                        <div className="relative mt-1">
                          <Bed size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          <Input
                            type="number"
                            value={budgetForm.bedrooms}
                            onChange={(e) => setBudgetForm({ ...budgetForm, bedrooms: e.target.value })}
                            placeholder="e.g. 2"
                            className="pl-9 bg-white"
                            data-testid="bedrooms-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-stone-600 text-xs">Bathrooms</Label>
                        <div className="relative mt-1">
                          <Bath size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          <Input
                            type="number"
                            value={budgetForm.bathrooms}
                            onChange={(e) => setBudgetForm({ ...budgetForm, bathrooms: e.target.value })}
                            placeholder="e.g. 2"
                            className="pl-9 bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isCommercial && (
                  <div className="p-4 bg-amber-50 rounded-xl space-y-3">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                      <Briefcase size={14} />
                      Commercial Details
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-stone-600 text-xs">Workstations</Label>
                        <div className="relative mt-1">
                          <Monitor size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          <Input
                            type="number"
                            value={budgetForm.workstations}
                            onChange={(e) => setBudgetForm({ ...budgetForm, workstations: e.target.value })}
                            placeholder="20"
                            className="pl-9"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-stone-600 text-xs">Cabins</Label>
                        <Input
                          type="number"
                          value={budgetForm.cabins}
                          onChange={(e) => setBudgetForm({ ...budgetForm, cabins: e.target.value })}
                          placeholder="3"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-600 text-xs">Conf. Rooms</Label>
                        <div className="relative mt-1">
                          <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          <Input
                            type="number"
                            value={budgetForm.conference_rooms}
                            onChange={(e) => setBudgetForm({ ...budgetForm, conference_rooms: e.target.value })}
                            placeholder="1"
                            className="pl-9"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isIndustrial && (
                  <div className="p-4 bg-green-50 rounded-xl space-y-3">
                    <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                      <Factory size={14} />
                      Industrial Details
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-stone-600 text-xs">Production Zones</Label>
                        <Input
                          type="number"
                          value={budgetForm.production_zones}
                          onChange={(e) => setBudgetForm({ ...budgetForm, production_zones: e.target.value })}
                          placeholder="2"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-600 text-xs">Storage Areas</Label>
                        <Input
                          type="number"
                          value={budgetForm.storage_areas}
                          onChange={(e) => setBudgetForm({ ...budgetForm, storage_areas: e.target.value })}
                          placeholder="1"
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-stone-600 text-xs">Office Area (sq.ft)</Label>
                        <Input
                          type="number"
                          value={budgetForm.office_area_sqft}
                          onChange={(e) => setBudgetForm({ ...budgetForm, office_area_sqft: e.target.value })}
                          placeholder="500"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Floor & Lift */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-stone-700 font-medium text-sm">Floor Number</Label>
                    <Input
                      type="number"
                      value={budgetForm.floor_number}
                      onChange={(e) => setBudgetForm({ ...budgetForm, floor_number: e.target.value })}
                      placeholder="0"
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={budgetForm.has_lift_access}
                        onCheckedChange={(checked) => setBudgetForm({ ...budgetForm, has_lift_access: checked })}
                      />
                      <span className="text-sm text-stone-600">Lift Access for Material</span>
                    </label>
                  </div>
                </div>

                {/* MANDATORY: Power Backup Question */}
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={16} className="text-amber-600" />
                    <Label className="text-stone-700 font-medium text-sm">Do you require Power Backup? *</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      onClick={() => setBudgetForm({ ...budgetForm, requires_power_backup: true })}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        budgetForm.requires_power_backup === true
                          ? 'border-amber-500 bg-amber-100'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                      data-testid="power-backup-yes"
                    >
                      <span className={`font-medium ${budgetForm.requires_power_backup === true ? 'text-amber-700' : 'text-stone-600'}`}>Yes</span>
                    </button>
                    <button
                      onClick={() => setBudgetForm({ ...budgetForm, requires_power_backup: false })}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        budgetForm.requires_power_backup === false
                          ? 'border-stone-500 bg-stone-100'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                      data-testid="power-backup-no"
                    >
                      <span className={`font-medium ${budgetForm.requires_power_backup === false ? 'text-stone-700' : 'text-stone-600'}`}>No</span>
                    </button>
                  </div>
                  
                  {/* Backup Duration - only show if Yes selected */}
                  {budgetForm.requires_power_backup === true && (
                    <div className="mt-3">
                      <Label className="text-stone-600 text-sm">Backup Duration</Label>
                      <Select
                        value={String(budgetForm.backup_time_minutes)}
                        onValueChange={(value) => setBudgetForm({ ...budgetForm, backup_time_minutes: parseInt(value) })}
                      >
                        <SelectTrigger className="mt-1 bg-white" data-testid="backup-duration-select">
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">60 minutes (1 hour)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {budgetForm.requires_power_backup === null && (
                    <p className="text-xs text-amber-600 mt-2">
                      <AlertCircle size={12} className="inline mr-1" />
                      If not answered, power backup will be included as "Subject to confirmation"
                    </p>
                  )}
                </div>

                {/* HOME AUTOMATION QUESTION (Residential Only) */}
                {['residential_apartment', 'residential_villa', 'renovation'].includes(budgetForm.project_type) && (
                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Home size={16} className="text-purple-600" />
                      <Label className="text-stone-700 font-medium text-sm">Do you want Home Automation?</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        onClick={() => setBudgetForm({ ...budgetForm, wants_home_automation: true })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          budgetForm.wants_home_automation === true
                            ? 'border-purple-500 bg-purple-100'
                            : 'border-stone-200 hover:border-stone-300 bg-white'
                        }`}
                        data-testid="home-automation-yes"
                      >
                        <span className={`font-medium ${budgetForm.wants_home_automation === true ? 'text-purple-700' : 'text-stone-600'}`}>Yes</span>
                      </button>
                      <button
                        onClick={() => setBudgetForm({ ...budgetForm, wants_home_automation: false })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          budgetForm.wants_home_automation === false
                            ? 'border-stone-500 bg-stone-100'
                            : 'border-stone-200 hover:border-stone-300 bg-white'
                        }`}
                        data-testid="home-automation-no"
                      >
                        <span className={`font-medium ${budgetForm.wants_home_automation === false ? 'text-stone-700' : 'text-stone-600'}`}>No</span>
                      </button>
                    </div>
                    
                    {budgetForm.wants_home_automation === true && (
                      <div className="mt-3">
                        <Label className="text-stone-600 text-sm">Automation Quality Tier</Label>
                        <Select
                          value={budgetForm.home_automation_tier}
                          onValueChange={(value) => setBudgetForm({ ...budgetForm, home_automation_tier: value })}
                        >
                          <SelectTrigger className="mt-1 bg-white" data-testid="automation-tier-select">
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="budget">Budget (₹150-200/sqft)</SelectItem>
                            <SelectItem value="standard">Standard (₹300-450/sqft)</SelectItem>
                            <SelectItem value="premium">Premium (₹600-900/sqft)</SelectItem>
                            <SelectItem value="luxury">Luxury (₹1,200-2,000/sqft)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {/* MODULAR KITCHEN QUESTION (Residential Only - SEPARATE FROM CARPENTRY) */}
                {['residential_apartment', 'residential_villa', 'renovation'].includes(budgetForm.project_type) && (
                  <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-2 mb-3">
                      <ChefHat size={16} className="text-orange-600" />
                      <Label className="text-stone-700 font-medium text-sm">Do you want a Modular Kitchen?</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        onClick={() => setBudgetForm({ ...budgetForm, wants_modular_kitchen: true })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          budgetForm.wants_modular_kitchen === true
                            ? 'border-orange-500 bg-orange-100'
                            : 'border-stone-200 hover:border-stone-300 bg-white'
                        }`}
                        data-testid="modular-kitchen-yes"
                      >
                        <span className={`font-medium ${budgetForm.wants_modular_kitchen === true ? 'text-orange-700' : 'text-stone-600'}`}>Yes</span>
                      </button>
                      <button
                        onClick={() => setBudgetForm({ ...budgetForm, wants_modular_kitchen: false })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          budgetForm.wants_modular_kitchen === false
                            ? 'border-stone-500 bg-stone-100'
                            : 'border-stone-200 hover:border-stone-300 bg-white'
                        }`}
                        data-testid="modular-kitchen-no"
                      >
                        <span className={`font-medium ${budgetForm.wants_modular_kitchen === false ? 'text-stone-700' : 'text-stone-600'}`}>No</span>
                      </button>
                    </div>
                    
                    {budgetForm.wants_modular_kitchen === true && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <Label className="text-stone-600 text-sm">Kitchen Quality Tier</Label>
                          <Select
                            value={budgetForm.modular_kitchen_tier}
                            onValueChange={(value) => setBudgetForm({ ...budgetForm, modular_kitchen_tier: value })}
                          >
                            <SelectTrigger className="mt-1 bg-white" data-testid="kitchen-tier-select">
                              <SelectValue placeholder="Select tier" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="budget">Budget (₹6,000-8,000/ft)</SelectItem>
                              <SelectItem value="standard">Standard (₹9,000-12,000/ft)</SelectItem>
                              <SelectItem value="premium">Premium (₹14,000-18,000/ft)</SelectItem>
                              <SelectItem value="luxury">Luxury (₹22,000-35,000/ft)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-stone-600 text-sm">Kitchen Running Length (ft)</Label>
                          <Input
                            type="number"
                            value={budgetForm.kitchen_running_ft}
                            onChange={(e) => setBudgetForm({ ...budgetForm, kitchen_running_ft: parseInt(e.target.value) || 10 })}
                            placeholder="10"
                            className="mt-1 bg-white"
                            data-testid="kitchen-length-input"
                          />
                          <p className="text-xs text-stone-500 mt-1">Standard assumption: 10 running ft</p>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-orange-600 mt-2">
                      <AlertCircle size={12} className="inline mr-1" />
                      Modular Kitchen is separate from Carpentry
                    </p>
                  </div>
                )}

                {/* Quality Tier */}
                <div>
                  <Label className="text-stone-700 font-medium text-sm">Quality Tier</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {QUALITY_TIERS.map((tier) => (
                      <button
                        key={tier.value}
                        onClick={() => setBudgetForm({ ...budgetForm, quality_tier: tier.value })}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          budgetForm.quality_tier === tier.value
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        data-testid={`quality-${tier.value}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Crown size={12} className={budgetForm.quality_tier === tier.value ? 'text-violet-600' : 'text-stone-400'} />
                          <span className="font-medium text-stone-900 text-sm">{tier.label}</span>
                        </div>
                        <p className="text-xs text-violet-600 font-medium">{tier.range}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Special Requirements */}
                <div>
                  <Label className="text-stone-700 font-medium text-sm">Special Requirements</Label>
                  <Textarea
                    value={budgetForm.special_requirements}
                    onChange={(e) => setBudgetForm({ ...budgetForm, special_requirements: e.target.value })}
                    placeholder="Smart home, Italian marble..."
                    className="mt-1"
                    rows={2}
                  />
                </div>

                <Button
                  onClick={handleBudgetEstimation}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-5 font-medium shadow-lg"
                  data-testid="generate-budget-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles size={18} />
                      Generate Budget
                      {usage && !usage.is_unlimited && usage.free_remaining === 0 && (
                        <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs" data-testid="generate-budget-credit-cost">100 Credits</span>
                      )}
                    </span>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-stone-700 font-medium text-sm">Project Type</Label>
                  <Select 
                    value={timelineForm.project_type} 
                    onValueChange={(v) => setTimelineForm({ ...timelineForm, project_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-stone-700 font-medium text-sm">Area (sq.ft) *</Label>
                    <Input
                      type="number"
                      value={timelineForm.area_sqft}
                      onChange={(e) => setTimelineForm({ ...timelineForm, area_sqft: e.target.value })}
                      placeholder="1500"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700 font-medium text-sm">Budget (₹) *</Label>
                    <Input
                      type="number"
                      value={timelineForm.budget}
                      onChange={(e) => setTimelineForm({ ...timelineForm, budget: e.target.value })}
                      placeholder="2500000"
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleTimelineEstimation}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-5 font-medium shadow-lg"
                >
                  {loading ? 'Generating...' : (
                    <span className="flex items-center gap-2">
                      <Clock size={18} />
                      Generate Timeline
                    </span>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Results Section - 3 columns */}
          <div className="lg:col-span-3 space-y-4">
            {mode === 'budget' && budgetResult && (
              <>
                {/* Summary Card */}
                <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl" data-testid="budget-result">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-violet-200 text-sm">Total Estimated Budget</p>
                      <h2 className="text-4xl font-bold mt-1">
                        ₹{(budgetResult.summary?.total_budget || 0).toLocaleString()}
                      </h2>
                      {selectedCategories.size > 0 && getSelectedTotal() !== (budgetResult.summary?.total_budget || 0) && (
                        <div className="mt-2 p-2 bg-white/10 rounded-lg">
                          <p className="text-violet-200 text-xs">Selected Categories Total</p>
                          <p className="text-2xl font-bold text-green-300">₹{getSelectedTotal().toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-violet-200 text-sm">Cost per sq.ft</p>
                      <p className="text-2xl font-bold">
                        ₹{(budgetResult.summary?.cost_per_sqft || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {budgetResult.summary?.top_3_categories && (
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {budgetResult.summary.top_3_categories.map((cat, idx) => (
                        <div key={idx} className="bg-white/10 rounded-lg p-3">
                          <p className="text-xs text-violet-200 truncate">{cat.name}</p>
                          <p className="font-bold">₹{(cat.amount / 100000).toFixed(1)}L</p>
                          <p className="text-xs text-violet-300">{cat.percentage}%</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {budgetResult.contingency && (
                    <div className={`mt-4 p-3 rounded-lg ${
                      budgetResult.contingency.risk_level === 'high' ? 'bg-red-500/20' :
                      budgetResult.contingency.risk_level === 'medium' ? 'bg-amber-500/20' : 'bg-green-500/20'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm flex items-center gap-2">
                          <Shield size={14} />
                          Contingency ({budgetResult.contingency.risk_level} risk)
                        </span>
                        <span className="font-bold">{budgetResult.contingency.percentage}%</span>
                      </div>
                      <p className="text-xs text-violet-200 mt-1">{budgetResult.contingency.reason}</p>
                    </div>
                  )}
                </div>

                {/* Budget Breakdown - FIXED SCROLL */}
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-stone-100 bg-stone-50">
                    <h3 className="font-bold text-stone-900 flex items-center gap-2">
                      <ListChecks size={18} className="text-violet-600" />
                      Execution-Ready Breakdown
                    </h3>
                    <p className="text-xs text-stone-500 mt-1">Sequential execution order • Click to expand • Uncheck to exclude from estimate</p>
                  </div>
                  
                  {/* SCROLLABLE CONTAINER */}
                  <div ref={breakdownRef} className="max-h-[450px] overflow-y-auto">
                    <div className="divide-y divide-stone-100">
                      {(budgetResult.budget_breakdown || []).map((item, idx) => (
                        <div key={idx} className={`group ${!selectedCategories.has(item.category) ? 'opacity-50 bg-stone-50' : ''}`}>
                          <div className="flex items-center p-4 hover:bg-stone-50 transition-colors">
                            {/* Checkbox */}
                            <div className="mr-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedCategories.has(item.category)}
                                onCheckedChange={() => toggleCategorySelection(item.category)}
                                className="border-2"
                              />
                            </div>
                            
                            {/* Expandable content */}
                            <button
                              onClick={() => toggleCategory(item.category)}
                              className="flex-1 flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                {expandedCategories.has(item.category) ? 
                                  <ChevronDown size={16} className="text-violet-500" /> : 
                                  <ChevronRight size={16} className="text-stone-400" />
                                }
                                <div className="text-left">
                                  <span className="font-medium text-stone-800">{item.category}</span>
                                  {item.labour_included > 0 && (
                                    <span className="ml-2 text-xs text-stone-400">
                                      (labour ₹{(item.labour_included / 1000).toFixed(0)}K)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-xs bg-stone-100 px-2 py-1 rounded text-stone-600">{item.percentage}%</span>
                                <span className={`font-bold w-24 text-right ${selectedCategories.has(item.category) ? 'text-stone-900' : 'text-stone-400 line-through'}`}>
                                  ₹{(item.amount / 100000).toFixed(2)}L
                                </span>
                              </div>
                            </button>
                          </div>
                          
                          {/* EXPANDED CONTENT - SCROLLABLE */}
                          {expandedCategories.has(item.category) && (
                            <div className="bg-stone-50 px-4 pb-4 mx-4 mb-2 rounded-lg max-h-[300px] overflow-y-auto">
                              {/* Activities */}
                              {item.activities?.length > 0 && (
                                <div className="mb-3 pt-3">
                                  <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Activities → Tasks → BOQ</p>
                                  <div className="space-y-1">
                                    {item.activities.map((act, aidx) => (
                                      <div key={aidx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-stone-100">
                                        <div>
                                          <span className="text-stone-700">{act.name}</span>
                                          {act.location && <span className="text-xs text-stone-400 ml-2">({act.location})</span>}
                                          {act.quantity && <span className="text-xs text-blue-500 ml-2">{act.quantity}</span>}
                                        </div>
                                        <span className="text-stone-500 font-medium">₹{(act.estimated_cost / 1000).toFixed(0)}K</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Labour Breakdown */}
                              {item.labour_breakdown && (
                                <div className="mb-3 p-2 bg-amber-50 rounded-lg">
                                  <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                                    <Wrench size={12} />
                                    Labour ({item.labour_breakdown.total_labour_percentage})
                                  </p>
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div>Fabrication: ₹{(item.labour_breakdown.fabrication / 1000).toFixed(0)}K</div>
                                    <div>Installation: ₹{(item.labour_breakdown.installation / 1000).toFixed(0)}K</div>
                                    <div>Finishing: ₹{(item.labour_breakdown.finishing / 1000).toFixed(0)}K</div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Included/Excluded */}
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                {item.included?.length > 0 && (
                                  <div>
                                    <p className="font-semibold text-green-600 mb-1">✓ Included</p>
                                    <ul className="space-y-1 text-stone-600">
                                      {item.included.map((inc, i) => <li key={i}>• {inc}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {item.excluded?.length > 0 && (
                                  <div>
                                    <p className="font-semibold text-red-600 mb-1">✗ Excluded</p>
                                    <ul className="space-y-1 text-stone-600">
                                      {item.excluded.map((exc, i) => <li key={i}>• {exc}</li>)}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              
                              {item.risk_notes && (
                                <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700 flex items-start gap-2">
                                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                                  {item.risk_notes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Contingency Row with Checkbox */}
                    {budgetResult.contingency && budgetResult.contingency.amount > 0 && (
                      <div className={`border-t-2 border-violet-200 ${!selectedCategories.has('Contingency') ? 'opacity-50 bg-stone-50' : ''}`}>
                        <div className="flex items-center p-4 hover:bg-stone-50 transition-colors">
                          <div className="mr-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedCategories.has('Contingency')}
                              onCheckedChange={() => toggleCategorySelection('Contingency')}
                              className="border-2"
                            />
                          </div>
                          <div className="flex-1 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Shield size={16} className="text-amber-500" />
                              <div className="text-left">
                                <span className="font-medium text-stone-800">Contingency</span>
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                  budgetResult.contingency.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                                  budgetResult.contingency.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {budgetResult.contingency.risk_level} risk
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-xs bg-stone-100 px-2 py-1 rounded text-stone-600">{budgetResult.contingency.percentage}%</span>
                              <span className={`font-bold w-24 text-right ${selectedCategories.has('Contingency') ? 'text-stone-900' : 'text-stone-400 line-through'}`}>
                                ₹{(budgetResult.contingency.amount / 100000).toFixed(2)}L
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recommendations */}
                {budgetResult.recommendations?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm">
                    <h3 className="font-bold text-stone-900 mb-3 flex items-center gap-2">
                      <Lightbulb size={18} className="text-amber-500" />
                      Actionable Recommendations
                    </h3>
                    <div className="space-y-2">
                      {budgetResult.recommendations.map((rec, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                          rec.priority === 'high' ? 'bg-red-50 border-red-500' :
                          rec.priority === 'medium' ? 'bg-amber-50 border-amber-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <div className="flex items-start justify-between">
                            <p className="text-sm text-stone-700">{rec.action}</p>
                            {rec.potential_saving && (
                              <span className="text-xs text-green-600 font-medium">Save ₹{rec.potential_saving.toLocaleString()}</span>
                            )}
                          </div>
                          {rec.system_reference && (
                            <span className="text-xs text-violet-600 mt-1 inline-block">→ {rec.system_reference}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Terms & Conditions + Convert */}
                <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
                  <button 
                    onClick={() => setShowTerms(!showTerms)}
                    className="w-full p-4 flex items-center justify-between bg-stone-50 hover:bg-stone-100"
                  >
                    <span className="font-bold text-stone-900 flex items-center gap-2">
                      <FileText size={18} className="text-stone-600" />
                      Terms & Conditions
                    </span>
                    {showTerms ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  {showTerms && (
                    <div className="p-4">
                      <div className="h-48 overflow-y-auto border rounded-lg p-4 bg-stone-50">
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-xs text-stone-600">
                          {budgetResult.terms_and_conditions}
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox
                            checked={termsAccepted}
                            onCheckedChange={setTermsAccepted}
                            className="mt-0.5"
                          />
                          <span className="text-sm text-stone-700">
                            I acknowledge that labour, transportation, and handling charges are <strong>floor-wise and access-dependent</strong>. 
                            Base pricing assumes ground floor or lift access.
                          </span>
                        </label>
                      </div>
                      
                      {termsAccepted && budgetResult?.pricing?.can_convert === false && (
                        <Button
                          onClick={() => window.dispatchEvent(new CustomEvent('bc:insufficient-credits', { detail: { message: 'Purchase AI Credits to unlock Convert to Project with Tasks (100 credits).', required: 100, balance: credits?.balance ?? '—' } }))}
                          variant="outline"
                          className="w-full mt-4 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          data-testid="convert-locked-btn"
                        >
                          <Lock size={16} className="mr-2" />
                          Purchase AI Credits to unlock
                        </Button>
                      )}
                      {termsAccepted && budgetResult?.pricing?.can_convert !== false && (
                        <Button
                          onClick={() => (budgetResult?.pricing?.convert_credits ? setConfirmConvertOpen(true) : setShowConvertDialog(true))}
                          className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white"
                          data-testid="convert-to-project-btn"
                        >
                          <CheckCircle2 size={16} className="mr-2" />
                          Convert to Project with Tasks
                          {budgetResult?.pricing?.convert_credits ? <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">100 Credits</span> : null}
                        </Button>
                      )}
                      <Dialog open={confirmConvertOpen} onOpenChange={setConfirmConvertOpen}>
                        <DialogContent className="sm:max-w-sm" data-testid="convert-confirm-dialog">
                          <DialogHeader>
                            <DialogTitle>Use AI Credits?</DialogTitle>
                            <DialogDescription>This will use 100 AI Credits. Proceed?</DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmConvertOpen(false)} data-testid="convert-confirm-cancel">Cancel</Button>
                            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setConfirmConvertOpen(false); setShowConvertDialog(true); }} data-testid="convert-confirm-proceed">Proceed</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </>
            )}

            {mode === 'timeline' && timelineResult && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="timeline-result">
                <div className="text-center mb-6 pb-4 border-b border-stone-100">
                  <p className="text-stone-500 text-sm">Estimated Duration</p>
                  <h2 className="text-4xl font-bold text-stone-900 mt-1">
                    {timelineResult.total_duration_weeks} weeks
                  </h2>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {timelineResult.phases?.map((phase, idx) => (
                    <div key={idx} className="p-4 border border-stone-200 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-stone-900">{phase.name}</span>
                        <span className="text-sm text-violet-600 font-medium">{phase.duration_days} days</span>
                      </div>
                      <Progress value={(phase.duration_days / timelineResult.total_duration_days) * 100} className="h-2 mb-3" />
                      {phase.tasks && (
                        <div className="flex flex-wrap gap-1.5">
                          {phase.tasks.map((task, tidx) => (
                            <span key={tidx} className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded">
                              {task}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {((mode === 'budget' && !budgetResult) || (mode === 'timeline' && !timelineResult)) && (
              <div className="bg-gradient-to-br from-stone-50 to-violet-50 rounded-2xl border border-stone-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                  {mode === 'budget' ? 
                    <Calculator className="text-violet-500" size={32} /> : 
                    <Clock className="text-violet-500" size={32} />
                  }
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-2">
                  {mode === 'budget' ? 'Generate Smart Budget' : 'Plan Your Timeline'}
                </h3>
                <p className="text-stone-500 text-sm max-w-sm mx-auto">
                  {mode === 'budget' 
                    ? 'Fill project details for an execution-ready budget with activities that convert to Tasks → BOQ.'
                    : 'Enter parameters for an AI-powered timeline.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Convert to Project Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase size={20} className="text-violet-600" />
              Convert to Project
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Sharma Residence Interior"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Client Name *</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g., Mr. Sharma"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Details *</Label>
              <Input
                value={contactDetails}
                onChange={(e) => setContactDetails(e.target.value)}
                placeholder="e.g., +91 9876543210 or email@example.com"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">Phone number or email for project communication</p>
            </div>
            
            <div className="bg-violet-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-violet-900">This will create:</p>
              <ul className="text-violet-700 mt-2 space-y-1">
                <li>• New project with budget ₹{(budgetResult?.summary?.total_budget || 0).toLocaleString()}</li>
                <li>• {budgetResult?.budget_breakdown?.length || 0} category tasks with sub-tasks</li>
                <li>• Activities as task items ready for assignment</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToProject}
              disabled={converting || !projectName.trim() || !clientName.trim() || !contactDetails.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {converting ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Purchase Dialog */}
      <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="text-purple-600" size={20} />
              Purchase AI Credits
            </DialogTitle>
            <DialogDescription>
              Choose a credit pack to continue using AI Budget Estimation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {creditPacks.map((pack) => (
              <div 
                key={pack.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  pack.badge 
                    ? 'border-purple-300 bg-purple-50 ring-2 ring-purple-200' 
                    : 'border-stone-200 hover:border-purple-200'
                }`}
                onClick={() => handlePurchasePack(pack)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-stone-800">{pack.name}</h4>
                      {pack.badge && (
                        <Badge className="bg-purple-600 text-white text-xs">{pack.badge}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500">{pack.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">₹{pack.price}</p>
                    <p className="text-xs text-stone-400">₹{(pack.price / pack.credits).toFixed(0)}/credit</p>
                  </div>
                </div>
                {purchasingPack === pack.id && (
                  <div className="mt-2 text-sm text-purple-600 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Billing Adjustment Notice */}
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            <p className="font-medium">AI charges are adjusted if you proceed with the project.</p>
            <p className="text-xs mt-1 text-green-600">The AI Assistant fee will be fully adjusted in your final project billing upon confirmation.</p>
          </div>
          
          <div className="mt-4 pt-4 border-t text-center">
            <p className="text-sm text-stone-500 mb-2">Need credits but cannot purchase?</p>
            <Button 
              variant="outline" 
              onClick={() => { setShowCreditDialog(false); setShowRequestDialog(true); }}
            >
              Request Credits from Admin
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="text-green-600" size={20} />
              Request AI Credits
            </DialogTitle>
            <DialogDescription>
              Request free credits from admin. They will review and approve your request.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-stone-700">How many credits do you need?</Label>
              <Select value={String(creditsRequested)} onValueChange={(v) => setCreditsRequested(parseInt(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 credits</SelectItem>
                  <SelectItem value="5">5 credits</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-stone-700">Why do you need these credits?</Label>
              <Textarea
                value={creditRequestReason}
                onChange={(e) => setCreditRequestReason(e.target.value)}
                placeholder="e.g., Need to estimate budget for my upcoming renovation project..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestCredits} className="bg-green-600 hover:bg-green-700">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
